import { Button, Form, Modal } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { buildFolderTree, folderTreeValues } from "../helpers/folder-tree.ts"
import { listEveryFolder } from "../helpers/list-every-folder.ts"
import { type FileItem } from "../types/file-item.types.ts"
import { type FolderListItem } from "../types/folder.types.ts"
import { FolderTreeSelect } from "./FolderTreeSelect.tsx"

// TreeSelect reads a `null` value as "nothing selected", so the root needs a real one.
const ROOT = "root"

interface MoveFileModalProps {
  // Omitted means the company workspace (Phase C1) — the destination list is
  // narrowed to this team's company folders, never a project's folder tree
  // (a cross-workspace move is a business-context change, not a plain move).
  projectId?: string
  files: FileItem[]
  onClose: () => void
  onSubmit: (folderId: string | null) => Promise<void>
}

export function MoveFileModal({
  projectId,
  files,
  onClose,
  onSubmit
}: MoveFileModalProps) {
  const { t } = useTranslation()
  const [folders, setFolders] = useState<FolderListItem[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [folderId, setFolderId] = useState<string | null>(
    files.length === 1 ? files[0].folderId : null
  )
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    void listEveryFolder(
      projectId ? { project: projectId } : { location: "company" }
    ).then((response) => {
      if (cancelled) return
      setFolders(response.ok && response.data ? response.data.results || [] : [])
      setLoadFailed(!response.ok || !response.data)
    })
    return () => {
      cancelled = true
    }
  }, [projectId, attempt])

  const retry = () => {
    setFolders(null)
    setLoadFailed(false)
    setAttempt((count) => count + 1)
  }

  const treeData = useMemo(
    () => [{ title: t("files.move.root"), value: ROOT }, ...buildFolderTree(folders ?? [])],
    [folders, t]
  )
  const selectable = useMemo(() => new Set(folderTreeValues(treeData)), [treeData])

  const loading = folders === null
  // antd prints a value it has no node for as the raw id.
  const shownValue = selectable.has(folderId ?? ROOT) ? (folderId ?? ROOT) : undefined

  const title =
    files.length === 1
      ? t("files.move.title", { name: files[0].name })
      : t("files.move.titleMany", {
          count: files.length
        })

  const handleOk = async () => {
    setSubmitting(true)
    try {
      await onSubmit(folderId)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      title={title}
      onCancel={onClose}
      onOk={() => void handleOk()}
      confirmLoading={submitting}
      okText={t("files.move.confirm")}
      okButtonProps={{ disabled: loading || shownValue === undefined }}
    >
      <Form layout="vertical">
        <Form.Item
          label={t("files.move.destination")}
          validateStatus={loadFailed ? "error" : undefined}
          help={
            loadFailed ? (
              <>
                {t("files.move.foldersFailed")}{" "}
                <Button type="link" size="small" className="p-0" onClick={retry}>
                  {t("files.move.retry")}
                </Button>
              </>
            ) : undefined
          }
        >
          <FolderTreeSelect
            // rc-tree expands every branch on its first mount only; a retried list remounts it.
            key={attempt}
            aria-label={t("files.move.destination")}
            treeData={treeData}
            notFoundContent={t("files.search.noResults")}
            loading={loading}
            disabled={loading}
            value={shownValue}
            onChange={(value) => setFolderId(value === ROOT ? null : value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
